import { NarrowPageContentContainer } from "@vyne-shared/components/vyneNavigation/PageContentContainers";
import { Navigate, createFileRoute } from "@tanstack/react-router";
import { Result } from "antd";
import { getCredential } from "@vyne-shared/utils/getCredential";
import { validateStripeParams } from "utils/validateStripeParams";
import type { PatientPortalPatientLoginCredentials } from "api/VynePayments/requests";

const IndexPage = () => {
    const params = Route.useSearch();

    // If this is a legacy link, we'll have initial credentials here and redirect using them (with an unnecessary but
    // slightly beneficial side effect of storing them).
    // If this is a Stripe redirect link, we count on credentials having already been stored and getting them back out
    // again to redirect with.
    const initialCredential = params && "token" in params ? params : undefined;
    const credential = getCredential<PatientPortalPatientLoginCredentials>(initialCredential);

    if (credential) {
        // If no channel was provided, default to "x" which means unknown.
        // Pass along any search parameters (other than legacy link credentials, just for aesthetics).
        return (
            <Navigate
                to="/$token/$channel"
                params={{ token: credential.token, channel: credential.channel ?? "x" }}
                search={params && "payment_intent" in params ? params : undefined}
            />
        );
    } else {
        return (
            <NarrowPageContentContainer>
                <Result
                    status="403"
                    title="Unable to log in"
                    subTitle="Please use the link you were sent in an email or text message to access the patient portal."
                />
            </NarrowPageContentContainer>
        );
    }
};

/** Route for the index page, which is only used for either legacy links using search parameters or Stripe redirects. */
export const Route = createFileRoute("/")({
    validateSearch: (search) => {
        if (typeof search.token === "string" && search.token) {
            const credentials: PatientPortalPatientLoginCredentials = {
                token: search.token,
                channel: typeof search.channel === "string" ? search.channel : null,
            };

            return credentials;
        }

        return validateStripeParams(search);
    },
    component: IndexPage,
});
